import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  CheckCircle,
  Loader2,
  Mail,
  Edit2,
  Package,
  Truck,
  Clock,
  AlertTriangle,
  ArrowRight,
  Paperclip,
  RefreshCw,
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const MAIL_CLASS_OPTIONS = [
  { value: 'certified_mail', label: 'Certified Mail w/ Return Receipt' },
  { value: 'certified_mail_restricted', label: 'Certified Mail - Restricted Delivery' },
  { value: 'priority_mail', label: 'Priority Mail' },
  { value: 'first_class', label: 'First Class Mail' },
  { value: 'registered_mail', label: 'Registered Mail' },
  { value: 'other', label: 'Other' },
];

const URGENCY_COLORS = {
  high: 'bg-red-100 text-red-800 border-red-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  low: 'bg-blue-50 text-blue-800 border-blue-200',
};

const MailingTracker = ({ conversationId, mailingStep, stepLabel, linkedDocuments, onSwitchToStrategy }) => {
  const { toast } = useToast();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [checkingDelivery, setCheckingDelivery] = useState(false);

  // Delivery / countdown state
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [nextStep, setNextStep] = useState(null);

  // Form state
  const [trackingNumber, setTrackingNumber] = useState('');
  const [mailClass, setMailClass] = useState('certified_mail');
  const [mailedAt, setMailedAt] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [responseWindowDays, setResponseWindowDays] = useState(mailingStep === 'second_mailing' ? 10 : 21);

  const fetchRecord = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API_BASE}/api/mailing-tracker/${conversationId}?step=${mailingStep}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.data.success && res.data.record) {
        const r = res.data.record;
        setRecord(r);
        setTrackingNumber(r.tracking_number || '');
        setMailClass(r.mail_class || 'certified_mail');
        setMailedAt(r.mailed_at ? r.mailed_at.split('T')[0] : new Date().toISOString().split('T')[0]);
        setNotes(r.notes || '');
        setResponseWindowDays(r.response_window_days || (mailingStep === 'second_mailing' ? 10 : 21));

        if (r.delivery_status) {
          setDeliveryInfo({
            status: r.delivery_status,
            delivered_date: r.actual_delivery || r.delivered_date,
            usps_status: r.usps_status,
            expected_delivery: r.expected_delivery,
            last_check: r.last_tracking_check,
          });
        }
      }
    } catch (err) { /* no record */ }
    finally { setLoading(false); }
  }, [conversationId, mailingStep]);

  // Fetch countdown on load if record exists
  const fetchCountdown = useCallback(async () => {
    if (!record?.completion_status) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API_BASE}/api/mailing-tracker/${conversationId}/countdown/${mailingStep}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.data.success) {
        setCountdown(res.data.countdown);
        setNextStep(res.data.next_step);
      }
    } catch (e) { /* */ }
  }, [conversationId, mailingStep, record]);

  useEffect(() => { fetchRecord(); }, [fetchRecord]);
  useEffect(() => { fetchCountdown(); }, [fetchCountdown]);

  const handleSave = async () => {
    if (!trackingNumber.trim()) {
      toast({ title: 'Tracking Number Required', description: 'Please enter a valid tracking number.', variant: 'destructive' });
      return;
    }
    if (trackingNumber.trim().length < 5) {
      toast({ title: 'Invalid Tracking Number', description: 'Tracking number must be at least 5 characters.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE}/api/mailing-tracker/${conversationId}/log`,
        {
          mailing_step: mailingStep,
          step_label: stepLabel,
          tracking_number: trackingNumber.trim(),
          mail_class: mailClass,
          mailed_at: mailedAt,
          notes: notes.trim(),
          completion_status: true,
          linked_documents: linkedDocuments || [],
          response_window_days: parseInt(responseWindowDays) || 21,
        },
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      toast({ title: 'Mailing Logged Successfully', description: `${stepLabel} marked as complete.` });
      setEditing(false);
      await fetchRecord();
    } catch (err) {
      toast({ title: 'Failed to Save', description: err.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleCheckDelivery = async () => {
    setCheckingDelivery(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_BASE}/api/mailing-tracker/${conversationId}/check-delivery/${mailingStep}`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.data.success) {
        setDeliveryInfo({
          status: res.data.delivery_status,
          delivered_date: res.data.delivered_date,
          usps_status: res.data.usps_status,
          expected_delivery: res.data.expected_delivery,
          tracking_events: res.data.tracking_events,
          last_check: new Date().toISOString(),
        });
        setCountdown(res.data.countdown);
        setNextStep(res.data.next_step);

        if (res.data.is_delivered) {
          toast({ title: 'Package Delivered', description: `Delivered on ${new Date(res.data.delivered_date).toLocaleDateString()}. Response countdown started.` });
        } else {
          toast({ title: 'Tracking Updated', description: `Status: ${res.data.usps_status || res.data.delivery_status}` });
        }
      } else {
        toast({ title: 'Tracking Unavailable', description: res.data.error || 'Unable to check delivery status.', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Check Failed', description: err.response?.data?.detail || 'USPS tracking unavailable.', variant: 'destructive' });
    } finally { setCheckingDelivery(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div>;
  }

  const isCompleted = record?.completion_status === true;

  // Completed view with delivery + countdown + next step
  if (isCompleted && !editing) {
    return (
      <div className="mt-4 border-t pt-4 space-y-3" data-testid={`mailing-tracker-${mailingStep}`}>
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
            <Mail className="w-4 h-4 text-green-600" />
            Mailing Tracking & Completion
          </h4>
          <Badge className="bg-green-100 text-green-700 text-[10px]">
            <CheckCircle className="w-3 h-3 mr-1" />Completed
          </Badge>
        </div>

        {/* Tracking Details Card */}
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-3 space-y-2">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-gray-500">Tracking #:</span>
                <p className="font-mono font-medium text-gray-900 break-all">{record.tracking_number}</p>
              </div>
              <div>
                <span className="text-gray-500">Class:</span>
                <p className="font-medium text-gray-900">{MAIL_CLASS_OPTIONS.find(o => o.value === record.mail_class)?.label || record.mail_class}</p>
              </div>
              <div>
                <span className="text-gray-500">Mailed On:</span>
                <p className="font-medium text-gray-900">{record.mailed_at ? new Date(record.mailed_at).toLocaleDateString() : 'N/A'}</p>
              </div>
              {record.notes && (
                <div>
                  <span className="text-gray-500">Notes:</span>
                  <p className="font-medium text-gray-900 break-words">{record.notes}</p>
                </div>
              )}
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="ghost" size="sm" className="text-xs h-7 text-gray-500" onClick={() => setEditing(true)} data-testid={`edit-mailing-${mailingStep}`}>
                <Edit2 className="w-3 h-3 mr-1" /> Edit
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Delivery Status + Check Button */}
        <Card className="border-blue-200">
          <CardContent className="p-3 space-y-2">
            <div className="flex items-center justify-between">
              <h5 className="text-xs font-bold text-gray-700 flex items-center gap-1">
                <Truck className="w-3.5 h-3.5 text-blue-600" />
                USPS Delivery Status
              </h5>
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-[10px] px-2"
                onClick={handleCheckDelivery}
                disabled={checkingDelivery}
                data-testid={`check-delivery-${mailingStep}`}
              >
                {checkingDelivery ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                {checkingDelivery ? 'Checking...' : 'Check Status'}
              </Button>
            </div>

            {deliveryInfo ? (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Badge className={`text-[10px] ${
                    deliveryInfo.status === 'delivered' ? 'bg-green-100 text-green-700' :
                    deliveryInfo.status === 'in_transit' || deliveryInfo.status === 'mailed' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {deliveryInfo.status === 'delivered' ? 'Delivered' : deliveryInfo.usps_status || deliveryInfo.status || 'Mailed'}
                  </Badge>
                  {deliveryInfo.delivered_date && (
                    <span className="text-[10px] text-gray-500">on {new Date(deliveryInfo.delivered_date).toLocaleDateString()}</span>
                  )}
                  {deliveryInfo.expected_delivery && !deliveryInfo.delivered_date && (
                    <span className="text-[10px] text-gray-500">Expected: {new Date(deliveryInfo.expected_delivery).toLocaleDateString()}</span>
                  )}
                </div>
                {deliveryInfo.tracking_events?.length > 0 && (
                  <div className="space-y-0.5 text-[10px] text-gray-600 max-h-20 overflow-y-auto">
                    {deliveryInfo.tracking_events.slice(0, 3).map((ev, i) => (
                      <div key={i} className="flex gap-1.5">
                        <span className="text-gray-400 flex-shrink-0">{ev.timestamp ? new Date(ev.timestamp).toLocaleDateString() : ''}</span>
                        <span className="break-words">{ev.description || ev.status}</span>
                      </div>
                    ))}
                  </div>
                )}
                {deliveryInfo.last_check && (
                  <p className="text-[9px] text-gray-400">Last checked: {new Date(deliveryInfo.last_check).toLocaleString()}</p>
                )}
              </div>
            ) : (
              <p className="text-[10px] text-gray-400">Click "Check Status" to get live USPS delivery updates.</p>
            )}
          </CardContent>
        </Card>

        {/* Countdown Timer */}
        {countdown?.has_countdown && (
          <Card className={`border-2 ${countdown.status === 'expired' ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}`}>
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <Clock className={`w-4 h-4 ${countdown.status === 'expired' ? 'text-red-600' : 'text-amber-600'}`} />
                <span className="text-xs font-bold text-gray-800">Response Countdown</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className={`text-2xl font-bold ${countdown.status === 'expired' ? 'text-red-700' : 'text-amber-700'}`}>
                  {countdown.status === 'expired' ? 'EXPIRED' : `${countdown.days_remaining} day${countdown.days_remaining !== 1 ? 's' : ''}`}
                </span>
                <span className="text-xs text-gray-500">
                  {countdown.status === 'expired' ? 'No response received' : 'remaining to respond'}
                </span>
              </div>
              {countdown.response_due && (
                <p className="text-[10px] text-gray-500 mt-1">Due: {new Date(countdown.response_due).toLocaleDateString()}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Next Step Suggestion */}
        {nextStep?.has_suggestion && (
          <Card className={`border ${URGENCY_COLORS[nextStep.urgency] || URGENCY_COLORS.low}`}>
            <CardContent className="p-3">
              <div className="flex items-start gap-2">
                <ArrowRight className={`w-4 h-4 mt-0.5 flex-shrink-0 ${nextStep.urgency === 'high' ? 'text-red-600' : nextStep.urgency === 'medium' ? 'text-amber-600' : 'text-blue-600'}`} />
                <div className="min-w-0 space-y-1">
                  <p className="text-xs font-bold text-gray-900">{nextStep.title}</p>
                  <p className="text-[11px] text-gray-700 break-words">{nextStep.description}</p>
                  <p className="text-[11px] font-medium text-blue-700 break-words">{nextStep.action}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Evidence Upload Link */}
        <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
          <Paperclip className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
          <p className="text-[11px] text-gray-600 flex-1">
            Upload your mailing receipt screenshot as evidence in the <strong>Strategy Brain</strong> tab.
          </p>
          {onSwitchToStrategy && (
            <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2 text-blue-600" onClick={onSwitchToStrategy} data-testid={`upload-receipt-${mailingStep}`}>
              Upload Receipt
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Input form view
  return (
    <div className="mt-4 border-t pt-4" data-testid={`mailing-tracker-${mailingStep}`}>
      <h4 className="text-sm font-bold text-gray-800 flex items-center gap-1.5 mb-3">
        <Mail className="w-4 h-4 text-blue-600" />
        Mailing Tracking & Completion
      </h4>
      <Card className="border-blue-200">
        <CardContent className="p-3 space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Tracking Number *</label>
            <Input
              value={trackingNumber}
              onChange={e => setTrackingNumber(e.target.value)}
              placeholder="e.g., 7021 1970 0002 1234 5678"
              className="text-sm font-mono"
              data-testid={`tracking-number-${mailingStep}`}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Class of Mail</label>
              <select
                value={mailClass}
                onChange={e => setMailClass(e.target.value)}
                className="w-full text-xs border rounded-md px-2 py-1.5 bg-white"
                data-testid={`mail-class-${mailingStep}`}
              >
                {MAIL_CLASS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Mailing Date</label>
              <Input type="date" value={mailedAt} onChange={e => setMailedAt(e.target.value)} className="text-xs" data-testid={`mailed-at-${mailingStep}`} />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Notes (optional)</label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any additional notes..." className="text-xs min-h-[50px]" data-testid={`notes-${mailingStep}`} />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Response Window (days)</label>
            <div className="flex items-center gap-2">
              <select
                value={responseWindowDays}
                onChange={e => setResponseWindowDays(e.target.value)}
                className="w-full text-xs border rounded-md px-2 py-1.5 bg-white"
                data-testid={`response-window-${mailingStep}`}
              >
                <option value={3}>3 days</option>
                <option value={7}>7 days</option>
                <option value={10}>10 days</option>
                <option value={14}>14 days</option>
                <option value={21}>21 days</option>
                <option value={30}>30 days</option>
                <option value={45}>45 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
              </select>
            </div>
            <p className="text-[10px] text-gray-400 mt-1">How many days the respondent has to reply after delivery</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="w-full bg-blue-700 hover:bg-blue-800" data-testid={`mark-complete-${mailingStep}`}>
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><CheckCircle className="w-4 h-4 mr-2" />Mark Mailing as Complete</>}
          </Button>
          {editing && (
            <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => setEditing(false)}>Cancel Edit</Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


export const MailingProgressTracker = ({ conversationId }) => {
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${API_BASE}/api/mailing-tracker/${conversationId}/status`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (res.data.success) setStatuses(res.data.statuses);
      } catch (e) { /* */ }
      finally { setLoading(false); }
    };
    if (conversationId) fetch();
  }, [conversationId]);

  if (loading) return null;

  const steps = [
    { key: 'first_mailing', label: 'First Mailing' },
    { key: 'second_mailing', label: 'Second Mailing' },
    { key: 'third_mailing', label: 'Third Mailing' },
  ];
  const completed = steps.filter(s => statuses[s.key]?.completed).length;
  const pct = Math.round((completed / 3) * 100);

  return (
    <div className="bg-white border rounded-lg p-3 space-y-2" data-testid="mailing-progress-tracker">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
          <Package className="w-3.5 h-3.5 text-blue-600" />
          Administrative Mailing Progress
        </h4>
        <span className="text-[10px] text-gray-500">{completed}/3 complete ({pct}%)</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-1.5">
        <div className="bg-blue-600 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="flex gap-2">
        {steps.map(s => {
          const done = statuses[s.key]?.completed;
          return (
            <div key={s.key} className={`flex-1 text-center p-1.5 rounded text-[10px] font-medium ${done ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-50 text-gray-400 border border-gray-200'}`}>
              {done && <CheckCircle className="w-3 h-3 inline mr-0.5" />}
              {s.label}
              {done && statuses[s.key]?.tracking_number && (
                <p className="text-[8px] font-mono text-green-600 truncate mt-0.5">{statuses[s.key].tracking_number}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MailingTracker;
